import React, {useState, useCallback, useMemo } from "react";
import Box from "@material-ui/core/Box";
import Avatar from "@material-ui/core/Avatar";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Fade from "@material-ui/core/Fade";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import MenuIcon from "@material-ui/icons/Menu";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import FlightIcon from "@material-ui/icons/Flight";
import HotelIcon from "@material-ui/icons/Hotel";
import PublicIcon from "@material-ui/icons/Public";
import PaymentIcon from "@material-ui/icons/Payment";
import useStyles from "./styles";
import MobileNavDrawer from "../11/mobile-navbar";
import LoginSignUpModal from "../../../Login/loginSignUpModal";
import DefaultLogo from "../../../../../assets/images/defaultLogo.png";
import { thumborImageUrl} from "../../../../../Utility";

// Constants
const NAVBAR_RIGHT_ITEMS = ["Flights", "Hotels", "Packages", "Pay Safely"];
const MAX_ARTICLES = 6;

const ICONS_MAP = {
  Flights: FlightIcon,
  Hotels: HotelIcon,
  Packages: PublicIcon,
  "Pay Safely": PaymentIcon,
};

/**
 * Navigation Component with optimized performance and error handling
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.websiteData - Website configuration data
 * @param {Array} props.widgetList - List of widgets
 */
const Component12 = ({ websiteData = {}, widgetList = [] }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:1200px)");

  // Destructure props with safe defaults
  const {
    username = "",
    articles = [],
    memberProfile = {},
    userProfileData = {},
    handleLogout = () => {},
    isLoggedIn = false,
    content_arr: contentArr = [],
  } = websiteData;

  const { logo_url = "" } = memberProfile;
  const { userName = "" } = userProfileData;

  // Consolidated state management
  const [menuState, setMenuState] = useState({
    anchorElMenu: null,
    anchorElUser: null,
    dropdownAnchor: null,
    dropdownContent: [],
    showMenuDrawer: false,
    showLoginModal: false,
  });

  // Memoized transformed articles - only include if articles exist
  const transformedArticles = useMemo(() => {
    if (!Array.isArray(articles) || articles.length === 0) return [];

    return articles
      .filter((item) => item?.title && item?.slug_url) // Only valid articles
      .slice(0, MAX_ARTICLES)
      .map((item) => ({
        name: item.title,
        link: `/${username ? `${username}/` : ""}pages/${item.slug_url}`,
      }));
  }, [articles, username]);

  // Memoized navbar left content - conditionally include blogs
  const navbarLeftContent = useMemo(() => {
    const content = [
      {
        name: "Know More",
        id: "knowMore",
        isDropdown: true,
        dropDownItem: [
          {
            name: "About Us",
            link: `/${username ? `${username}/` : ""}about`,
            reloadLink: true,
          },
          {
            name: "Contact Us",
            link: `/${username ? `${username}/` : ""}contact`,
          },
          {
            name: "Terms and conditions",
            link: `/${
              username ? `${username}/` : ""
            }pages/terms-and-conditions`,
            reloadLink: true,
          },
        ],
        isShow: true,
      },
      {
        name: "Discover",
        id: "discover",
        isDropdown: true,
        dropDownItem: [
          {
            name: "News",
            link: `/${username ? `${username}/` : ""}news`,
          },
          {
            name: "Video Tours",
            link: `/${username ? `${username}/` : ""}destination-guides`,
          },
          {
            name: "Gallery",
            link: `/${username ? `${username}/` : ""}gallery_detail`,
          },
        ],
        isShow: true,
      },
    ];

    // Only add Blogs section if articles are available
    if (transformedArticles.length > 0) {
      content.splice(1, 0, {
        name: "Blogs",
        id: "blogs",
        isDropdown: true,
        dropDownItem: transformedArticles,
        isShow: true,
      });
    }

    return content.filter((item) => item.isShow);
  }, [username, transformedArticles]);

  // Memoized content filtering
  const loginDropdownItems = useMemo(() => {
    if (!Array.isArray(contentArr)) return [];
    const loginObj = contentArr.find((obj) => obj?.name === "Login");
    return loginObj?.dropDownItem || [];
  }, [contentArr]);

  const filteredRightNavItems = useMemo(() => {
    if (!Array.isArray(contentArr)) return [];
    return contentArr.filter(
      (obj) => obj?.name && NAVBAR_RIGHT_ITEMS.includes(obj.name)
    );
  }, [contentArr]);

  // Event handlers
  const updateMenuState = useCallback((updates) => {
    setMenuState((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleOpenDropdown = useCallback(
    (event, dropdownContent) => {
      event.preventDefault();
      updateMenuState({
        dropdownAnchor: event.currentTarget,
        dropdownContent: dropdownContent || [],
      });
    },
    [updateMenuState]
  );

  const handleCloseDropdown = useCallback(() => {
    updateMenuState({
      dropdownAnchor: null,
      dropdownContent: [],
    });
  }, [updateMenuState]);

  const handleOpenUserMenu = useCallback(
    (event) => {
      updateMenuState({ anchorElUser: event.currentTarget });
    },
    [updateMenuState]
  );

  const handleCloseUserMenu = useCallback(() => {
    updateMenuState({ anchorElUser: null });
  }, [updateMenuState]);

  const handleToggleDrawer = useCallback(() => {
    updateMenuState({ showMenuDrawer: !menuState.showMenuDrawer });
  }, [menuState.showMenuDrawer, updateMenuState]);

  const handleToggleLogin = useCallback(() => {
    updateMenuState({ showLoginModal: !menuState.showLoginModal });
  }, [menuState.showLoginModal, updateMenuState]);

  const handleLogoClick = useCallback(() => {
    window.location.href = `/${username ? username : ""}`;
  }, [username]);

  const handleLogoutClick = useCallback(() => {
    handleCloseUserMenu();
    handleLogout();
  }, [handleLogout, handleCloseUserMenu]);

  // Render helper for menu items
  const renderMenuItem = useCallback(
    (item, index) => {
      if (!item) return null;

      const { name = "", link = "", id = "" } = item;
      const key = id || `menu-item-${index}`;

      if (id === "logout") {
        return (
          <MenuItem key={key} onClick={handleLogoutClick}>
            {name}
          </MenuItem>
        );
      }

      if (link) {
        return (
          <MenuItem
            key={key}
            style={{ textTransform: "capitalize" }}
            component="a"
            href={link}
            onClick={handleCloseDropdown}
          >
            {name}
          </MenuItem>
        );
      }

      return <MenuItem key={key}>{name}</MenuItem>;
    },
    [handleLogoutClick, handleCloseDropdown]
  );

  // Render helper for right nav items
  const renderRightNavItem = useCallback(
    (item) => {
      if (!item?.name) return null;

      const { name, id, link } = item;
      const Icon = ICONS_MAP[name];

      return (
        <a href={link || "#"} key={id || name}>
          <Button className={classes.rightNavBtn}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              {Icon && <Icon />}
              {name}
            </div>
          </Button>
        </a>
      );
    },
    [classes.rightNavBtn]
  );

  // Render helper for left nav dropdown items
  const renderLeftNavItem = useCallback(
    (item) => {
      if (!item?.dropDownItem?.length) return null;

      const { name, id, dropDownItem } = item;

      return (
        <Box
          key={id}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Box
            aria-controls={`${id}-menu`}
            aria-haspopup="true"
            onClick={(e) => handleOpenDropdown(e, dropDownItem)}
            display="flex"
            alignItems="center"
            style={{ cursor: "pointer" }}
          >
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
              className={classes.leftDropdownItem}
            >
              {name}
              <ExpandMoreIcon style={{ color: "inherit" }} />
            </a>
          </Box>
        </Box>
      );
    },
    [classes.leftDropdownItem, handleOpenDropdown]
  );

  // Render user menu button
  const renderUserSection = useCallback(() => {
    if (isLoggedIn && userName) {
      const userInitial = userName.charAt(0).toUpperCase();

      return (
        <>
          <IconButton onClick={handleOpenUserMenu}>
            <Avatar
              style={{
                color: theme.agentTheme?.primary?.foreground,
                background: theme.agentTheme?.primary?.main,
              }}
            >
              {userInitial}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={menuState.anchorElUser}
            open={Boolean(menuState.anchorElUser)}
            onClose={handleCloseUserMenu}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            TransitionComponent={Fade}
          >
            {loginDropdownItems.map((item, index) => {
              if (!item) return null;

              if (item.id === "logout") {
                return (
                  <MenuItem key={item.id} onClick={handleLogoutClick}>
                    {item.name}
                  </MenuItem>
                );
              }

              if (item.link) {
                return (
                  <MenuItem
                    key={item.id || index}
                    component="a"
                    href={item.link}
                    onClick={handleCloseUserMenu}
                  >
                    {item.name}
                  </MenuItem>
                );
              }

              return (
                <MenuItem key={item.id || index} onClick={handleCloseUserMenu}>
                  {item.name}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      );
    }

    return (
      <Button
        onClick={handleToggleLogin}
        startIcon={<LockOpenIcon />}
        color="primary"
        variant="outlined"
        style={{
          borderRadius: "20px",
          textTransform: "none",
          fontWeight: 600,
        }}
      >
        Login
      </Button>
    );
  }, [
    isLoggedIn,
    userName,
    theme,
    menuState.anchorElUser,
    loginDropdownItems,
    handleOpenUserMenu,
    handleCloseUserMenu,
    handleToggleLogin,
    handleLogoutClick,
  ]);

  return (
    <>
      <Box className={classes.header}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {/* Left Navigation */}
          <Box display="flex" alignItems="center" style={{ gap: "1.5rem" }}>
            <IconButton
              className={classes.menu}
              onClick={handleToggleDrawer}
              aria-label="Open menu"
            >
              <MenuIcon className={classes.menuIcon} />
            </IconButton>

            <img
              className={classes.logoImg}
              src={logo_url ? thumborImageUrl(logo_url, 0, 100) : DefaultLogo}
              alt="logo"
              onClick={handleLogoClick}
              style={{ cursor: "pointer" }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = DefaultLogo;
              }}
            />

            {!isMobile && navbarLeftContent.length > 0 && (
              <>{navbarLeftContent.map(renderLeftNavItem)}</>
            )}
          </Box>

          {/* Right Navigation */}
          <Box
            className={classes.rightNav}
            display="flex"
            alignItems="center"
            style={{ gap: "1rem" }}
          >
            <Box
              className={classes.rightNavbarItems}
              display="flex"
              alignItems="center"
              style={{ gap: "0.5rem" }}
            >
              {filteredRightNavItems.map(renderRightNavItem)}
            </Box>

            {renderUserSection()}
          </Box>
        </Box>
      </Box>

      {/* Dropdown Menu */}
      <Menu
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        anchorEl={menuState.dropdownAnchor}
        open={Boolean(menuState.dropdownAnchor)}
        onClose={handleCloseDropdown}
        TransitionComponent={Fade}
      >
        {menuState.dropdownContent.length > 0 && (
          menuState.dropdownContent.map(renderMenuItem)
        ) 
        // : (
        //   <MenuItem disabled>No items available</MenuItem>
        // )
        }
      </Menu>

      {/* Mobile Drawer */}
      <MobileNavDrawer
        widgetList={widgetList}
        websiteData={websiteData}
        showDrawer={menuState.showMenuDrawer}
        setShowDrawer={(value) => updateMenuState({ showMenuDrawer: value })}
        contentArr={contentArr}
      />

      {/* Login Modal */}
      {menuState.showLoginModal && (
        <LoginSignUpModal
          onClose={handleToggleLogin}
          websiteData={websiteData}
        />
      )}
    </>
  );
};

Component12.displayName = "NavigationComponent";

export default React.memo(Component12);
